import { useQuery } from '@tanstack/react-query'
import { Skeleton } from '@/components/ui/skeleton'
import { type SubscriptionConditions, useSubscriptionConditions } from '@/hooks/use-subscription-conditions'
import {
  type ConditionsPreview,
  type ConditionsSnapshot,
  type CreditBenefit,
  formatBenefit,
  getCreditTypes,
} from '@/lib/credits-api'
import { formatCredits } from '@/lib/subscriptions'
import { cn } from '@/lib/utils'

function ConditionDetails({ value }: { value: ConditionsSnapshot }) {
  const types = useQuery({
    queryKey: ['credit-types'],
    queryFn: getCreditTypes,
  })
  return (
    <div className='space-y-1 text-sm'>
      <p className='font-medium'>
        {(value.price_cents / 100).toLocaleString('de-AT', {
          style: 'currency',
          currency: 'EUR',
        })} / Monat
      </p>
      {value.benefits.map((benefit) => (
        <p key={benefit.credit_type_id}>
          {formatBenefit({
            ...benefit,
            credit_name: types.data?.find((t) =>
              t.id === benefit.credit_type_id
            )?.name,
          })}
        </p>
      ))}
    </div>
  )
}

export function ConditionsComparison(
  { preview }: { preview: ConditionsPreview },
) {
  return (
    <div className='space-y-4'>
      <div className='rounded-md border p-3'>
        <p className='mb-2 font-semibold'>Neue Konditionen</p>
        <ConditionDetails value={preview.target} />
      </div>
      <p>{preview.subscriptions.length} betroffene Abos</p>
      {preview.subscriptions.map((s) => (
        <div key={s.id} className='space-y-2 rounded-md border p-3'>
          <p className='font-semibold'>
            {s.name} · ab {s.effective_at
              ? new Date(s.effective_at).toLocaleString('de-AT')
              : 'Termin unbekannt'}
          </p>
          <p className='text-sm text-muted-foreground'>
            Bisherige Konditionen:
          </p>
          <ConditionDetails value={s.current} />
          {s.conflict && (
            <p className='text-sm text-destructive'>
              Ein Tarifwechsel ist bereits vorgemerkt oder der Abrechnungstermin
              fehlt. Bitte zuerst im Abo klären.
            </p>
          )}
          {s.affected_bookings.length > 0 && (
            <div className='text-sm text-muted-foreground'>
              <p>Diese Buchungen bleiben trotz geringerem Kontingent oder entfallender Credit-Art erhalten:</p>
              <ul className='list-inside list-disc'>
                {s.affected_bookings.map((b) => (
                  <li key={b.id}>
                    {b.course_name} ·{' '}
                    {new Date(b.starts_at).toLocaleString('de-AT')}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ))}
      <p className='text-sm text-muted-foreground'>
        Alle bestehenden Buchungen bleiben erhalten und zählen weiter zum Wochenverbrauch. Gekauftes
        Guthaben wird nicht automatisch eingesetzt. Betroffene Kunden erhalten
        eine Benachrichtigung.
      </p>
    </div>
  )
}

function PendingConditions({ s }: { s: SubscriptionConditions }) {
  if (!s.pending) return null
  return (
    <div className='border-t pt-3'>
      <p className='mb-2 font-semibold'>
        Ab {new Date(s.effective_at!).toLocaleString('de-AT')}
      </p>
      <ConditionDetails value={s.pending} />
    </div>
  )
}

function ScheduledChange({ s }: { s: SubscriptionConditions }) {
  if (!s.scheduled_change) return null
  return (
    <div className='rounded-md border border-primary/30 bg-primary/5 p-3'>
      <p className='mb-2 text-sm font-semibold'>
        Tarifwechsel vorgemerkt: {s.scheduled_change.package_name} ab{' '}
        {new Date(s.scheduled_change.effective_at).toLocaleDateString('de-AT')}
      </p>
      {s.scheduled_change.conditions && <ConditionDetails value={s.scheduled_change.conditions} />}
    </div>
  )
}

export function SubscriptionConditionsInline(
  { packageName }: { packageName: string },
) {
  const query = useSubscriptionConditions()
  const s = query.data?.find((c) => c.package_name === packageName)
  if (!s) return null
  return (
    <div className='space-y-3' aria-label='Vereinbarte Abo-Konditionen'>
      <div>
        <p className='mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground'>
          Vereinbarte Konditionen
        </p>
        <ConditionDetails value={s.current} />
      </div>
      <PendingConditions s={s} />
      <ScheduledChange s={s} />
    </div>
  )
}

function formatBenefitAmount(benefit: CreditBenefit) {
  if (benefit.kind === 'unlimited') return 'Unbegrenzt / Woche'
  const amount = formatCredits(Number(benefit.amount ?? 0))
  return benefit.kind === 'weekly' ? `${amount} / Woche` : amount
}

// Agreed per-type credit allowances of a member's active subscription.
export function SubscriptionBenefitList(
  { userId, packageName, className }: { userId: string; packageName: string; className?: string },
) {
  const query = useSubscriptionConditions(userId)
  const types = useQuery({
    queryKey: ['credit-types'],
    queryFn: getCreditTypes,
  })
  const s = query.data?.find((c) => c.package_name === packageName)
  if (query.isLoading) return <Skeleton className='h-10 w-full' />
  if (!s?.current.benefits.length) return null
  const renderBenefits = (benefits: CreditBenefit[], muted = false) => (
    <dl className='space-y-1.5 text-sm'>
      {benefits.map((benefit) => (
        <div key={benefit.credit_type_id} className='flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5'>
          <dt className='text-muted-foreground'>
            {types.data?.find((t) => t.id === benefit.credit_type_id)?.name ?? 'Credits'}
          </dt>
          <dd className={cn('text-right font-semibold', muted ? 'text-muted-foreground' : 'text-foreground')}>
            {formatBenefitAmount(benefit)}
          </dd>
        </div>
      ))}
    </dl>
  )
  return (
    <div className={cn('space-y-3', className)}>
      {renderBenefits(s.current.benefits)}
      <PendingConditions s={s} />
      <ScheduledChange s={s} />
      {!s.is_current && (
        <div className='space-y-1.5 border-t pt-3'>
          <p className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
            Aktueller Tarif
          </p>
          {renderBenefits(s.catalog.benefits, true)}
        </div>
      )}
    </div>
  )
}
